import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, nothing, svg, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

const registry = {
    'it-android-square': () => import('./it-android-square-CS9Jm6D6.js'),
    'it-android': () => import('./it-android-CM_jremm.js'),
    'it-apple-square': () => import('./it-apple-square-CGes5mXG.js'),
    'it-apple': () => import('./it-apple-Bkz6a5Zc.js'),
    'it-arrow-down-circle': () => import('./it-arrow-down-circle-I6jWec3x.js'),
    'it-arrow-down-triangle': () => import('./it-arrow-down-triangle-CC_2NjvY.js'),
    'it-arrow-down': () => import('./it-arrow-down-CKDXUv8J.js'),
    'it-arrow-left-circle': () => import('./it-arrow-left-circle-BMrwkBri.js'),
    'it-arrow-left-triangle': () => import('./it-arrow-left-triangle-IBF7HkHH.js'),
    'it-arrow-left': () => import('./it-arrow-left-CnfHkuCJ.js'),
    'it-arrow-right-circle': () => import('./it-arrow-right-circle-Dif3sPcx.js'),
    'it-arrow-right-triangle': () => import('./it-arrow-right-triangle-BR3Mqigv.js'),
    'it-arrow-right': () => import('./it-arrow-right-CPIsiurT.js'),
    'it-arrow-up-circle': () => import('./it-arrow-up-circle-DQI0chwo.js'),
    'it-arrow-up-triangle': () => import('./it-arrow-up-triangle-DSciwQX-.js'),
    'it-arrow-up': () => import('./it-arrow-up-dUp-1v9b.js'),
    'it-ban': () => import('./it-ban-DKV99_48.js'),
    'it-behance': () => import('./it-behance-Bw2eUGN3.js'),
    'it-bluesky': () => import('./it-bluesky-DB-QLs0s.js'),
    'it-bookmark': () => import('./it-bookmark-DRcxVOiN.js'),
    'it-box': () => import('./it-box-Bvr_uX3S.js'),
    'it-burger': () => import('./it-burger-Epx3OLvi.js'),
    'it-calendar': () => import('./it-calendar-Di65OfV4.js'),
    'it-camera': () => import('./it-camera-BVrBWjh3.js'),
    'it-car': () => import('./it-car-DYwyDx_7.js'),
    'it-card': () => import('./it-card-DlgFWqB6.js'),
    'it-cart': () => import('./it-cart-BEcPRn__.js'),
    'it-chart-line': () => import('./it-chart-line-4YM-ohES.js'),
    'it-check-circle': () => import('./it-check-circle-DtEh4Q_G.js'),
    'it-check': () => import('./it-check-M1SFvWw8.js'),
    'it-chevron-left': () => import('./it-chevron-left-hMTkyhUn.js'),
    'it-chevron-right': () => import('./it-chevron-right-Ca_EBQ4S.js'),
    'it-clip': () => import('./it-clip-C-wpX-gK.js'),
    'it-clock': () => import('./it-clock-BBelwG_8.js'),
    'it-close-big': () => import('./it-close-big-DLY08syf.js'),
    'it-close-circle': () => import('./it-close-circle-Bz9ccZnJ.js'),
    'it-close': () => import('./it-close-D7B7LVFn.js'),
    'it-code-circle': () => import('./it-code-circle-Bo0lZs8T.js'),
    'it-collapse': () => import('./it-collapse-PVvRxrrA.js'),
    'it-comment': () => import('./it-comment-ew-oPZ0O.js'),
    'it-copy': () => import('./it-copy-C9mYMxcE.js'),
    'it-delete': () => import('./it-delete-JdkbrC5q.js'),
    'it-designers-italia': () => import('./it-designers-italia-BquhP1XE.js'),
    'it-download': () => import('./it-download-CIWVmTiT.js'),
    'it-error': () => import('./it-error-C6RzCrv3.js'),
    'it-exchange-circle': () => import('./it-exchange-circle-Dl-gcRZl.js'),
    'it-expand': () => import('./it-expand-CVSATaS2.js'),
    'it-external-link': () => import('./it-external-link-DeszhEEB.js'),
    'it-facebook-square': () => import('./it-facebook-square-DhAz4P8U.js'),
    'it-facebook': () => import('./it-facebook-D4eNOYV-.js'),
    'it-figma-square': () => import('./it-figma-square-DRDd1bDV.js'),
    'it-figma': () => import('./it-figma-BHeUAd7p.js'),
    'it-file-audio': () => import('./it-file-audio-c2VLUgEY.js'),
    'it-file-compressed': () => import('./it-file-compressed-DF8mz05j.js'),
    'it-file-csv': () => import('./it-file-csv-CHdojhBR.js'),
    'it-file-docx': () => import('./it-file-docx-CXGFuiKk.js'),
    'it-file-image': () => import('./it-file-image-BrOk2t6d.js'),
    'it-file-json': () => import('./it-file-json-DnhfKPBa.js'),
    'it-file-odp': () => import('./it-file-odp-DdKkgd58.js'),
    'it-file-ods': () => import('./it-file-ods-DuVXPo_8.js'),
    'it-file-odt': () => import('./it-file-odt-Dbxvp-bG.js'),
    'it-file-pdf-ext': () => import('./it-file-pdf-ext-DO_SA473.js'),
    'it-file-pdf': () => import('./it-file-pdf-8_4e44it.js'),
    'it-file-ppt': () => import('./it-file-ppt-Cx0XFUZP.js'),
    'it-file-sheet': () => import('./it-file-sheet-CrbDVFo5.js'),
    'it-file-signed': () => import('./it-file-signed-KcQZtXBY.js'),
    'it-file-slides': () => import('./it-file-slides-BE94TT_B.js'),
    'it-file-txt': () => import('./it-file-txt-CdS__v4e.js'),
    'it-file-video': () => import('./it-file-video-Cm3zB3xV.js'),
    'it-file-xlsx': () => import('./it-file-xlsx-MecbsS6S.js'),
    'it-file-xml': () => import('./it-file-xml-Ny6qQM_8.js'),
    'it-file': () => import('./it-file-fsLJd0Pg.js'),
    'it-files': () => import('./it-files-D1x1LCc1.js'),
    'it-flag': () => import('./it-flag-BObBtSEJ.js'),
    'it-flickr-square': () => import('./it-flickr-square-BO5mtyle.js'),
    'it-flickr': () => import('./it-flickr-D_Xc7aak.js'),
    'it-folder': () => import('./it-folder-m3tmzGKL.js'),
    'it-fullscreen': () => import('./it-fullscreen-NgS52jcd.js'),
    'it-funnel': () => import('./it-funnel-DdtPUA_7.js'),
    'it-github': () => import('./it-github-BBz_ZPtW.js'),
    'it-google': () => import('./it-google-UyXDW-u9.js'),
    'it-hearing': () => import('./it-hearing-Dv-YIhHz.js'),
    'it-help-circle': () => import('./it-help-circle-CqKEgxIV.js'),
    'it-help': () => import('./it-help-B2GBYXDO.js'),
    'it-horn': () => import('./it-horn-rZQXlrnR.js'),
    'it-inbox': () => import('./it-inbox-Do6dhOGi.js'),
    'it-info-circle': () => import('./it-info-circle-Czj5d2bm.js'),
    'it-instagram': () => import('./it-instagram-D5aR_L0n.js'),
    'it-key': () => import('./it-key-DRxrET50.js'),
    'it-less-circle': () => import('./it-less-circle-CP6-Al2M.js'),
    'it-link': () => import('./it-link-DE90eaAg.js'),
    'it-linkedin-square': () => import('./it-linkedin-square-CHqrDMYp.js'),
    'it-linkedin': () => import('./it-linkedin-BGNYnn0o.js'),
    'it-list': () => import('./it-list-DJYxqMCK.js'),
    'it-lock': () => import('./it-lock-1vI5M-7l.js'),
    'it-locked': () => import('./it-locked-8cfuMzH6.js'),
    'it-logout': () => import('./it-logout-CwWjiFyD.js'),
    'it-mail-open': () => import('./it-mail-open-BbMHGxsT.js'),
    'it-mail': () => import('./it-mail-aPRyWnwz.js'),
    'it-map-marker-circle': () => import('./it-map-marker-circle-BRInhN1h.js'),
    'it-map-marker-minus': () => import('./it-map-marker-minus-71sRe33Y.js'),
    'it-map-marker-plus': () => import('./it-map-marker-plus-DsPwjQir.js'),
    'it-map-marker': () => import('./it-map-marker-2gjZoBr1.js'),
    'it-mastodon-square': () => import('./it-mastodon-square-Grmy2Lm2.js'),
    'it-mastodon': () => import('./it-mastodon-lQtt7SD1.js'),
    'it-maximize-alt': () => import('./it-maximize-alt-Bdz1MdNo.js'),
    'it-maximize': () => import('./it-maximize-DXZ6_N3P.js'),
    'it-medium-square': () => import('./it-medium-square-DzZD0mmH.js'),
    'it-medium': () => import('./it-medium-CAfBjUKV.js'),
    'it-minimize': () => import('./it-minimize-Bnj0BCG7.js'),
    'it-minus-circle': () => import('./it-minus-circle-B2Dm1NC0.js'),
    'it-minus': () => import('./it-minus-om5K6FiB.js'),
    'it-moodle-square': () => import('./it-moodle-square-BQ_Px3eS.js'),
    'it-moodle': () => import('./it-moodle-BIb2p6B_.js'),
    'it-more-actions': () => import('./it-more-actions-VCET7Nzo.js'),
    'it-more-items': () => import('./it-more-items-CjovIzea.js'),
    'it-note': () => import('./it-note-DxrmfuMN.js'),
    'it-open-source': () => import('./it-open-source-DygkL3Z7.js'),
    'it-pa': () => import('./it-pa-Bjb3z3oj.js'),
    'it-password-invisible': () => import('./it-password-invisible-fQJ_pVqB.js'),
    'it-password-visible': () => import('./it-password-visible-BZcXU4UE.js'),
    'it-pencil': () => import('./it-pencil-B9KD83Hb.js'),
    'it-piattaforme': () => import('./it-piattaforme-BiUeZjZk.js'),
    'it-pin': () => import('./it-pin-CBTHMlE9.js'),
    'it-pinterest-square': () => import('./it-pinterest-square-B-BdBicd.js'),
    'it-pinterest': () => import('./it-pinterest-0F51VxFX.js'),
    'it-plug': () => import('./it-plug-CnDTrLiy.js'),
    'it-plus-circle': () => import('./it-plus-circle-C1wQTarW.js'),
    'it-plus': () => import('./it-plus-D7lUBn02.js'),
    'it-presentation': () => import('./it-presentation-C4DFyb-F.js'),
    'it-print': () => import('./it-print-BvyiWATf.js'),
    'it-quora-square': () => import('./it-quora-square-DYqmziCW.js'),
    'it-quora': () => import('./it-quora-LkQaQgf2.js'),
    'it-reddit-square': () => import('./it-reddit-square-B9a0sndl.js'),
    'it-reddit': () => import('./it-reddit-Tn9ltgVI.js'),
    'it-refresh': () => import('./it-refresh-CB-7YV0I.js'),
    'it-restore': () => import('./it-restore-DkeYhSeS.js'),
    'it-rss-square': () => import('./it-rss-square-CtNRGa74.js'),
    'it-rss': () => import('./it-rss-CapSBFW-.js'),
    'it-search': () => import('./it-search-CKFFxM_L.js'),
    'it-settings': () => import('./it-settings-BchWbTHI.js'),
    'it-share': () => import('./it-share-DgOA3Wso.js'),
    'it-sign': () => import('./it-sign-DoP7_q98.js'),
    'it-slack-square': () => import('./it-slack-square-BXBOWOLY.js'),
    'it-slack': () => import('./it-slack-hrDBK_3P.js'),
    'it-snapchat-square': () => import('./it-snapchat-square-hHmKZJWy.js'),
    'it-snapchat': () => import('./it-snapchat-BDeuRQfJ.js'),
    'it-software': () => import('./it-software-Bvhe0OD1.js'),
    'it-spotify': () => import('./it-spotify-huUslGt0.js'),
    'it-stackexchange-square': () => import('./it-stackexchange-square-DSV7_t57.js'),
    'it-stackexchange': () => import('./it-stackexchange-WH3VXoQV.js'),
    'it-stackoverflow-square': () => import('./it-stackoverflow-square-gafES6I4.js'),
    'it-stackoverflow': () => import('./it-stackoverflow-D4G4nErK.js'),
    'it-star-full': () => import('./it-star-full-socWfZ0W.js'),
    'it-star-outline': () => import('./it-star-outline-Dz_EpxwQ.js'),
    'it-team-digitale': () => import('./it-team-digitale-ncXJjVKG.js'),
    'it-telegram': () => import('./it-telegram-CNKQnUwD.js'),
    'it-telephone': () => import('./it-telephone-D0NcEsYy.js'),
    'it-threads-square': () => import('./it-threads-square-CwHWnDpr.js'),
    'it-threads': () => import('./it-threads-wlVUHQza.js'),
    'it-tiktok-square': () => import('./it-tiktok-square-ahuKH2qk.js'),
    'it-tiktok': () => import('./it-tiktok-D0Im3Kkm.js'),
    'it-tool': () => import('./it-tool-C3q_ZzJH.js'),
    'it-twitter-square': () => import('./it-twitter-square-C_Nk75cD.js'),
    'it-twitter': () => import('./it-twitter-DuX6O0aB.js'),
    'it-unlocked': () => import('./it-unlocked-Bl8RZaWi.js'),
    'it-upload': () => import('./it-upload-CdyNmsJ7.js'),
    'it-user': () => import('./it-user-BReKDtFr.js'),
    'it-video': () => import('./it-video-mJ-BeKle.js'),
    'it-vimeo-square': () => import('./it-vimeo-square-Cy2PlOfw.js'),
    'it-vimeo': () => import('./it-vimeo-DW2ZVUA5.js'),
    'it-warning-circle': () => import('./it-warning-circle-ClB3FCc8.js'),
    'it-warning': () => import('./it-warning--LtOG5CH.js'),
    'it-whatsapp-square': () => import('./it-whatsapp-square-BiLM645v.js'),
    'it-whatsapp': () => import('./it-whatsapp-Bqr384Z7.js'),
    'it-wifi': () => import('./it-wifi-DdFKxT8d.js'),
    'it-youtube': () => import('./it-youtube-n895nR0E.js'),
    'it-zoom-in': () => import('./it-zoom-in-B2zZU9BC.js'),
    'it-zoom-out': () => import('./it-zoom-out-C3SGugZ1.js')
};
/**
 * Get a dynamic icon loader by name.
 */
const getIcon = (name) => registry[name];

var styles = css`.icon {
  width: var(--bsi-icon-size-m);
  height: var(--bsi-icon-size-m);
  fill: var(--bsi-icon-default);
  vertical-align: middle;
}
.icon.icon-padded {
  margin: 8px;
}
.icon.icon-xs {
  width: var(--bsi-icon-size-xs);
  height: var(--bsi-icon-size-xs);
}
.icon.icon-xs.icon-padded {
  margin: 4px;
}
.icon.icon-sm {
  width: var(--bsi-icon-size-s);
  height: var(--bsi-icon-size-s);
}
.icon.icon-sm.icon-padded {
  margin: 6px;
}
.icon.icon-lg {
  width: var(--bsi-icon-size-l);
  height: var(--bsi-icon-size-l);
}
.icon.icon-lg.icon-padded {
  margin: 12px;
}
.icon.icon-xl {
  width: var(--bsi-icon-size-xl);
  height: var(--bsi-icon-size-xl);
}
.icon.icon-xl.icon-padded {
  margin: 16px;
}

.icon-primary {
  fill: var(--bsi-icon-primary);
}

.icon-secondary {
  fill: var(--bsi-icon-secondary);
}

.icon-success {
  fill: var(--bsi-icon-success);
}

.icon-warning {
  fill: var(--bsi-icon-warning);
}

.icon-danger {
  fill: var(--bsi-icon-danger);
}

.icon-inverse {
  fill: var(--bsi-icon-inverse);
}

.icon-disabled {
  fill: var(--bsi-icon-disabled);
}

:root {
  --bsi-rounded-icon-size: var(--bsi-icon-size-s);
}

.rounded-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--bsi-rounded-icon-size);
  height: var(--bsi-rounded-icon-size);
  background-color: var(--bsi-icon-inverse);
  border-radius: var(--bsi-radius-circle);
}
.rounded-icon.rounded-primary {
  background-color: var(--bsi-icon-primary);
}
.rounded-icon.rounded-secondary {
  background-color: var(--bsi-icon-secondary);
}
.rounded-icon.rounded-success {
  background-color: var(--bsi-icon-success);
}
.rounded-icon.rounded-warning {
  background-color: var(--bsi-icon-warning);
}
.rounded-icon.rounded-danger {
  background-color: var(--bsi-icon-danger);
}
.rounded-icon.rounded-inverse {
  background-color: var(--bsi-icon-inverse);
}
.rounded-icon.rounded-disabled {
  background-color: var(--bsi-icon-disabled);
}
.rounded-icon .icon {
  margin-right: 0;
}

.align-top {
  vertical-align: top !important;
}

.align-middle {
  vertical-align: middle !important;
}

.align-bottom {
  vertical-align: bottom !important;
}

.bg-primary {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-primary-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-secondary {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-secondary-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-success {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-success-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-info {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-info-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-warning {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-warning-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-danger {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-danger-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-light {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-light-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-dark {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-dark-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-black {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-black-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-white {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-white-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-100 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-100-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-200 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-200-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-300 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-300-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-400 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-400-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-500 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-500-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-600 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-600-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-700 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-700-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-800 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-800-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-900 {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-900-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-body {
  --bsi-bg-opacity: 1;
  background-color: rgba(var(--bsi-body-bg-rgb), var(--bsi-bg-opacity)) !important;
}

.bg-transparent {
  --bsi-bg-opacity: 1;
  background-color: transparent !important;
}

:host {
  display: inline-block;
}

:host([align=top]) {
  vertical-align: top;
}

:host([align=middle]),
:host(:not([align])) {
  vertical-align: middle;
}

:host([align=bottom]) {
  vertical-align: bottom;
}

.icon-white {
  fill: #fff !important;
}
.icon-white path {
  fill: #fff !important;
}`;

let ItIcon = class ItIcon extends BaseComponent {
    constructor() {
        super(...arguments);
        // @property({ type: String }) background?: Colors;
        this.align = 'middle';
        this.label = '';
        this.padded = false;
    }
    async updated(changedProps) {
        if (changedProps.has('name') && this.name) {
            const loader = getIcon(this.name);
            if (loader) {
                const raw = (await loader()).default;
                this.parseAndStoreSvg(raw);
            }
        }
        if (changedProps.has('src') && this.src) {
            await this.loadSvgFromUrl(this.src);
        }
        if (changedProps.has('label')) {
            this.handleTitleId();
        }
        if (changedProps.has('label') ||
            changedProps.has('size') ||
            changedProps.has('color') ||
            // changedProps.has('background') ||
            changedProps.has('padded') ||
            changedProps.has('role') ||
            changedProps.has('align')) {
            this.updateSvgAttributes();
        }
    }
    handleTitleId() {
        if (this.label) {
            if (!this.titleId) {
                this.titleId = this.generateId('icon-label');
            }
        }
        else {
            this.titleId = undefined;
        }
    }
    async loadSvgFromUrl(url) {
        try {
            const res = await fetch(url);
            if (!res.ok)
                throw new Error(`Failed to load SVG from ${url} (status: ${res.status})`);
            const raw = await res.text();
            this.parseAndStoreSvg(raw);
        }
        catch (e) {
            this.svgElement = undefined;
            this.announceSvgLoadError();
        }
    }
    announceSvgLoadError() {
        this.dispatchEvent(new Event('error', {
            bubbles: false,
            composed: false,
            cancelable: false,
        }));
    }
    parseAndStoreSvg(raw) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(raw, 'image/svg+xml');
        const _svg = doc.documentElement;
        this.svgElement = _svg;
        this.updateSvgAttributes();
    }
    applySvgAttributes(svgEl) {
        svgEl.removeAttribute('width');
        svgEl.removeAttribute('height');
        const classList = this.updateClasses();
        svgEl.setAttribute('class', classList);
        svgEl.setAttribute('part', 'icon');
        // Accessibility logic:
        // - If a `label` is provided:
        //     - The icon is accessible to assistive technologies.
        //     - Remove aria-hidden attribute if it exists
        //     - Sets `role="img"` to indicate the SVG is an image.
        //     - Adds a <title> element with the label text inside the SVG.
        //     - Sets `aria-labelledby` to reference the <title> for screen readers.
        // - If no `label` is provided:
        //     - The icon is treated as decorative and ignored by assistive technologies.
        //     - Sets `aria-hidden="true".
        //     - Sets `role="presentation"` to indicate the SVG is purely decorative.
        //     - Removes any <title> and `aria-labelledby` attributes from the SVG.
        if (this.label) {
            svgEl.removeAttribute('aria-hidden');
            svgEl.setAttribute('role', 'img');
            // Remove previous title/aria-labelledby
            svgEl.removeAttribute('aria-labelledby');
            svgEl.querySelectorAll('title').forEach((t) => t.remove());
            // Add title and aria-labelledby for screen readers
            if (this.titleId) {
                const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
                titleEl.id = this.titleId;
                titleEl.textContent = this.label;
                svgEl.prepend(titleEl);
                svgEl.setAttribute('aria-labelledby', this.titleId);
            }
        }
        else {
            svgEl.setAttribute('aria-hidden', 'true');
            svgEl.setAttribute('role', 'presentation');
            svgEl.removeAttribute('aria-labelledby');
            svgEl.querySelectorAll('title').forEach((t) => t.remove());
        }
    }
    updateSvgAttributes() {
        if (!this.svgElement)
            return;
        this.applySvgAttributes(this.svgElement);
    }
    updateClasses() {
        return [
            'icon',
            this.size ? `icon-${this.size}` : '',
            this.color ? `icon-${this.color}` : '',
            // this.background ? `bg-${this.background}` : '',
            this.align ? `align-${this.align}` : '',
            this.padded ? `icon-padded` : '',
        ]
            .filter(Boolean)
            .join(' ')
            .trim();
    }
    handleSlotChange() {
        const nodes = this.slotEl.assignedNodes({ flatten: true });
        const _svg = nodes.find((node) => node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'svg');
        if (_svg) {
            this.applySvgAttributes(_svg);
        }
    }
    render() {
        return html `
      <slot @slotchange=${this.handleSlotChange}>${this.svgElement ? svg `${this.svgElement}` : nothing}</slot>
    `;
    }
};
ItIcon.styles = styles;
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItIcon.prototype, "size", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItIcon.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItIcon.prototype, "color", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItIcon.prototype, "align", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItIcon.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItIcon.prototype, "padded", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItIcon.prototype, "src", void 0);
__decorate([
    state(),
    __metadata("design:type", HTMLElement)
], ItIcon.prototype, "svgElement", void 0);
__decorate([
    query('slot'),
    __metadata("design:type", HTMLSlotElement)
], ItIcon.prototype, "slotEl", void 0);
ItIcon = __decorate([
    customElement('it-icon')
], ItIcon);

export { ItIcon };
//# sourceMappingURL=it-icon.js.map
